import {
  Component,
  Host,
  Listen,
  h,
  Prop,
  State,
  Event,
  EventEmitter,
  Element,
} from "@stencil/core";

import {
  StzhSkiplinkFocusEvent,
  StzhSkiplinkBlurEvent,
} from "../../index";

/**
 * @slot - Slot for any text content
 */
@Component({
  tag: "stzh-skiplink",
  styleUrl: "stzh-skiplink.scss",
  scoped: true
})
export class StzhSkiplink {
  /** Label */
  @Prop() label: string = "";

  /** href for linking to e.g. id of an element to skip to */
  @Prop() href: string;

  /** Access key of link (usually a number e.g. 1) */
  @Prop() linkAccesskey: string;

  /** Link title for description e.g. how to use skiplink with access key ([ALT + 1]) */
  @Prop() linkTitle: string;

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the link element.
   * Default value will be taken from `label` prop or default slot.
   */
  @Prop() analyticsId: string;

  /** Whether the skiplink is currently focused */
  @State() focused: boolean = false;

  /** Focus event */
  @Event() stzhFocus: EventEmitter<StzhSkiplinkFocusEvent>;

  /** Blur event */
  @Event() stzhBlur: EventEmitter<StzhSkiplinkBlurEvent>;

  @Element() element: HTMLStzhSkiplinkElement;

  @Listen('focusin')
  focusinHandler() {
    this.focused = true;
  }

  @Listen('focusout')
  focusoutHandler() {
    this.focused = false;
  }

  private link: HTMLAnchorElement;
  private text: HTMLDivElement;
  private focusedByInput: boolean = false;

  private onRootFocus = () => {
    if (!this.focusedByInput) {
      this.link.focus();
    }

    this.focusedByInput = false;
  }

  private onFocus = (event: FocusEvent) => {
    this.focusedByInput = true;

    // dispatch native focus event
    const focusEvent = new FocusEvent("focus", {
      view: window,
      bubbles: false,
      cancelable: false
    });

    this.element.dispatchEvent(focusEvent);

    // dispatch own focus event for updating flyingfocus position correctly
    this.stzhFocus.emit({
      component: "stzh-skiplink",
      originalEvent: event
    });
  }

  private onBlur = (event: FocusEvent) => {
    const blurEvent = new FocusEvent("blur", {
      view: window,
      bubbles: false,
      cancelable: false
    });

    this.element.dispatchEvent(blurEvent);

    this.stzhBlur.emit({
      component: "stzh-skiplink",
      originalEvent: event
    });
  }

  componentDidRender() {
    requestAnimationFrame(() => {
      if (this.link) {
        this.link?.setAttribute("s-object-id", this.analyticsId || this.label || this.text.innerText);

        // Override default click behavior
        this.link.addEventListener("click", (event) => this.handleSkipLinkClick(event));
      }
    });
  }

  private handleSkipLinkClick(event: Event): void {
    event.preventDefault(); // Prevent default anchor navigation

    if (!this.href) return; // No target

    // Find all matching and visible elements
    const targetElements = document.querySelectorAll(this.href);
    const visibleElements = Array.from(targetElements).filter((el) => {
      return el instanceof HTMLElement && window.getComputedStyle(el).display !== "none";
    });

    const targetElement = visibleElements.length > 0 ? visibleElements[0] : targetElements[0];

    if (targetElement instanceof HTMLElement) {
      targetElement.focus();
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });

      // Update URL manually to reflect the skipped section
      const newUrl =
        window.location.origin +          // e.g. "https://example.com"
        window.location.pathname +        // e.g. "/some/nested/path"
        window.location.search +          // e.g. "?query=123"
        this.href;                        // e.g. "#someElement"
      history.replaceState(null, "", newUrl);
    }
  }

  render() {
    return (
      <Host focused={this.focused} tabindex="-1" onFocus={this.onRootFocus}>
        <a
          ref={(el) => (this.link = el as HTMLAnchorElement)}
          class="stzh-skiplink"
          href={this.href}
          accessKey={this.linkAccesskey}
          title={this.linkTitle}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
        >
          <div ref={(el) => (this.text = el as HTMLDivElement)}>
            {this.label ? this.label : <slot></slot>}
          </div>
        </a>
      </Host>
    );
  }
}
